import { useState } from 'react';
import { auth } from '../../firebase';

// Normaliza a YYYY-MM-DD en horario local
const toLocalYMD = (d = new Date()) => {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const ExpenseForm = ({ onSave, onCancel, currentUser, openTurn = null, defaultAffectsCash = false }) => {
  const [expense, setExpense] = useState({
    category: '',
    amount: '',
    description: '',
    date: toLocalYMD(new Date()),
    registeredBy: typeof currentUser === 'string' ? currentUser : currentUser?.email || '',
    isRecurring: false,
    recurrencePeriod: 'mensual',
    affectsCash: !!defaultAffectsCash,
  });

  const categories = [
    'Arriendo',
    'Servicios Basicos',
    'Sueldos',
    'Transporte',
    'Marketing',
    'Mantenimiento',
    'Insumos',
    'Otros',
  ];
  const recurrencePeriods = ['Diario', 'Semanal', 'Mensual', 'Anual'];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setExpense((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!expense.category || !expense.amount || !expense.date) {
      alert('Por favor, completa Categoria, Monto y Fecha.');
      return;
    }

    // La fecha elegida es una fecha de negocio, no la hora en que se registra.
    // Se conserva exactamente el YYYY-MM-DD seleccionado para que un gasto
    // registrado hoy pueda pertenecer correctamente a un día anterior.
    const dateStr = String(expense.date || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      alert('La fecha del gasto no es válida. Selecciona nuevamente la fecha.');
      return;
    }

    const payload = {
      id: Date.now(),
      category: expense.category,
      amount: parseFloat(expense.amount),
      description: expense.description || '',
      date: dateStr,       // fecha de negocio seleccionada por el usuario
      dateStr,             // fuente de verdad para filtros/reportes
      registeredBy: expense.registeredBy,
      support: null, // soporte deshabilitado
      isRecurring: !!expense.isRecurring,
      recurrencePeriod: expense.recurrencePeriod || 'mensual',
      affectsCash: !!expense.affectsCash,
      turnId: expense.affectsCash && openTurn?.id ? openTurn.id : '',
      createdAt: new Date().toISOString(),
    };

    onSave(payload);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-8 max-w-xl mx-auto">
      <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Registrar Nuevo Gasto</h2>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Categoria*</label>
          <select
            name="category"
            value={expense.category}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          >
            <option value="">Selecciona una categoria</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Monto (COP)*</label>
          <input
            type="number"
            name="amount"
            value={expense.amount}
            onChange={handleChange}
            min="0"
            step="1"
            placeholder="Ej: 50000"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Descripcion (opcional)</label>
          <textarea
            name="description"
            value={expense.description}
            onChange={handleChange}
            rows="3"
            placeholder="Ej: Pago luz mes de Julio"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Fecha del Gasto*</label>
          <input
            type="date"
            name="date"
            value={expense.date}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>

        {/* Soporte de imagen removido temporalmente */}

        <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-3">
          <input
            id="affectsCash"
            type="checkbox"
            name="affectsCash"
            checked={expense.affectsCash}
            onChange={handleChange}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="affectsCash" className="text-sm text-gray-900">
            Afecta la caja de hoy
            {openTurn?.id ? (
              <span className="ml-2 text-xs text-gray-500">(turno: {openTurn.id.slice(0, 6)})</span>
            ) : (
              <span className="ml-2 text-xs text-amber-600">(no hay turno abierto)</span>
            )}
          </label>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            name="isRecurring"
            checked={expense.isRecurring}
            onChange={handleChange}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label className="ml-2 block text-sm text-gray-900">Gasto Recurrente</label>
        </div>

        {expense.isRecurring && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Periodo de Recurrencia</label>
            <select
              name="recurrencePeriod"
              value={expense.recurrencePeriod}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {recurrencePeriods.map((period) => (
                <option key={period} value={period.toLowerCase()}>{period}</option>
              ))}
            </select>
          </div>
        )}

        <div className="mt-2 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Registrar Gasto
          </button>
        </div>
      </form>
    </div>
  );
};

export default ExpenseForm;
